import { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { toast } from "sonner";
import { PenLine, Type, Eraser, Check, Upload } from "lucide-react";
import { removeWhiteBackground } from "@/lib/imageBg";

export default function SignaturePad({ open, onOpenChange, onSave, title = "Add your signature", saveEndpoint = "/profile/signature", saveLabel = "Signature" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [typed, setTyped] = useState("");
  const [tab, setTab] = useState("draw");
  const [saveToProfile, setSaveToProfile] = useState(true);
  const [uploaded, setUploaded] = useState(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file (PNG or JPG)"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image is too large (max 5MB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      // Auto-remove the white paper background so scans stamp cleanly on the form.
      const cleaned = await removeWhiteBackground(reader.result);
      setUploaded(cleaned);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const c = canvasRef.current;
      if (c) { const ctx = c.getContext("2d"); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0A2540"; }
    }, 100);
    return () => clearTimeout(t);
  }, [open, tab]);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
  };
  const start = (e) => { drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); };

  const confirm = () => {
    let dataUrl = null;
    if (tab === "type" && typed.trim()) {
      const c = document.createElement("canvas");
      c.width = 600; c.height = 160;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#0A2540";
      ctx.font = "64px 'Brush Script MT', 'Segoe Script', cursive";
      ctx.textBaseline = "middle";
      ctx.fillText(typed.trim(), 20, 90);
      dataUrl = c.toDataURL("image/png");
    } else if (tab === "draw") {
      dataUrl = canvasRef.current.toDataURL("image/png");
    } else if (tab === "upload" && uploaded) {
      dataUrl = uploaded;
    }
      if (dataUrl) {
        onSave(dataUrl);
        if (saveToProfile) {
          api.put(saveEndpoint, { signature: dataUrl })
            .then(() => toast.success(`${saveLabel} saved to your profile for reuse`))
            .catch(() => {});
        }
        onOpenChange(false); clear(); setTyped(""); setUploaded(null);
      }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid="signature-pad">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{title}</DialogTitle></DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 rounded-sm">
            <TabsTrigger value="draw" data-testid="sig-tab-draw" className="gap-1.5 rounded-sm"><PenLine size={14} /> Draw</TabsTrigger>
            <TabsTrigger value="type" data-testid="sig-tab-type" className="gap-1.5 rounded-sm"><Type size={14} /> Type</TabsTrigger>
            <TabsTrigger value="upload" data-testid="sig-tab-upload" className="gap-1.5 rounded-sm"><Upload size={14} /> Upload</TabsTrigger>
          </TabsList>
          <TabsContent value="draw">
            <canvas ref={canvasRef} width={432} height={180} data-testid="sig-canvas"
              className="w-full border-2 border-black bg-white touch-none cursor-crosshair"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
            <button onClick={clear} data-testid="sig-clear" className="text-xs text-muted-foreground hover:text-foreground mt-2 inline-flex items-center gap-1"><Eraser size={13} /> Clear</button>
          </TabsContent>
          <TabsContent value="type">
            <Input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="sig-type-input" placeholder="Type your name" className="rounded-sm" />
            <div className="mt-3 h-[100px] border-2 border-black bg-white grid place-items-center overflow-hidden">
              <span style={{ fontFamily: "'Brush Script MT','Segoe Script',cursive", fontSize: 44, color: "#0A2540" }}>{typed || "Your signature"}</span>
            </div>
          </TabsContent>
          <TabsContent value="upload">
            <label data-testid="sig-upload-label" className="flex flex-col items-center justify-center gap-2 h-[140px] border-2 border-dashed border-black bg-white cursor-pointer hover:bg-secondary/40 transition-colors">
              <Upload size={22} className="text-accent" />
              <span className="text-sm text-muted-foreground">Click to upload a signature image (PNG or JPG)</span>
              <input type="file" accept="image/*" onChange={onFile} data-testid="sig-upload-input" className="hidden" />
            </label>
            {uploaded && (
              <div className="mt-3 h-[80px] border-2 border-black bg-white grid place-items-center overflow-hidden">
                <img src={uploaded} alt="signature preview" data-testid="sig-upload-preview" className="max-h-full max-w-full object-contain" />
              </div>
            )}
            <p className="text-[0.7rem] text-muted-foreground mt-2">Tip: a transparent PNG signs cleanest on the form.</p>
          </TabsContent>
        </Tabs>
        <Button onClick={confirm} data-testid="sig-confirm" className="rounded-sm gap-2 mt-2 bg-accent text-accent-foreground hover:bg-accent/90">
          <Check size={16} /> Use this signature
        </Button>
        <label className="flex items-center gap-2 justify-center mt-1 cursor-pointer">
          <Checkbox checked={saveToProfile} onCheckedChange={(c) => setSaveToProfile(!!c)} data-testid="sig-save-profile" />
          <span className="text-xs text-muted-foreground">Save to my profile and reuse on every form</span>
        </label>
        <p className="text-[0.7rem] text-muted-foreground text-center">Your signature is placed on the form's signature line automatically.</p>
      </DialogContent>
    </Dialog>
  );
}
